import { SvgIcon } from '../../components/icons';
import { Badge, Tooltip } from '../primitives';
import { NAV_SECTIONS } from './navConfig';

type DesignSidebarProps = {
  /** Path of the active item — matched exactly, not by substring. */
  activePath: string;
  onNavigate: (path: string) => void;
  collapsed: boolean;
  /** True on tablet portrait, where the rail floats over content. */
  overlay?: boolean;
  user: { name: string; role: string; initials: string };
  onOpenSettings: () => void;
};

/**
 * Redesigned sidebar.
 *
 * What changed from the production `Draw`, and why:
 *
 * - **Grouped into five labelled sections** instead of 26 alphabetical links.
 *   See `navConfig.ts` for the reasoning.
 * - **Icons on every item**, so the collapsed rail is usable and scanning the
 *   expanded list is a glance rather than a read.
 * - **Collapses to a 56px rail** (the production drawer only slides fully off
 *   screen, so you trade all navigation for content width). Section labels
 *   become hairline rules in rail mode, so grouping survives the collapse.
 * - **Exact-path active state.** The production version uses
 *   `location.pathname.includes(route)`, which makes any route that is a
 *   substring of another light up both.
 * - **Pinned user tile** at the bottom — the production app has no user
 *   affordance in the sidebar at all.
 * - Tokenised colours. The production sidebar is a raw `bg-sky-600` that is
 *   not a project token and cannot be themed.
 */
export const DesignSidebar = ({
  activePath,
  onNavigate,
  collapsed,
  overlay = false,
  user,
  onOpenSettings,
}: DesignSidebarProps) => (
  <aside
    className="ds-sidebar"
    data-ds-collapsed={collapsed ? 'true' : undefined}
    data-ds-overlay={overlay ? 'true' : undefined}
    aria-label="Main navigation"
  >
    <nav className="ds-nav ds-scroll">
      {NAV_SECTIONS.map((section) => (
        <div className="ds-nav-section" key={section.title}>
          <div className="ds-nav-section-label" aria-hidden={collapsed}>
            {section.title}
          </div>
          <ul>
            {section.items.map((item) => {
              const isActive = activePath === item.path;
              const button = (
                <button
                  type="button"
                  className="ds-nav-item"
                  data-ds-active={isActive ? 'true' : undefined}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => onNavigate(item.path)}
                  style={{ width: '100%' }}
                >
                  <span className="ds-nav-icon">
                    <SvgIcon name={item.icon} size={16} />
                  </span>
                  <span className="ds-nav-label">{item.label}</span>
                  {item.badge !== undefined && (
                    <span className="ds-nav-trail">
                      <Badge count={item.badge} />
                    </span>
                  )}
                </button>
              );

              return (
                <li key={item.path}>
                  {/* In rail mode the tooltip is the only thing naming the
                      icon, so it must appear on focus as well as hover. */}
                  {collapsed ? (
                    <Tooltip
                      label={
                        item.badge
                          ? `${item.label} (${item.badge})`
                          : item.label
                      }
                      placement="right"
                    >
                      {button}
                    </Tooltip>
                  ) : (
                    button
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>

    <div className="ds-sidebar-footer">
      <button type="button" className="ds-user-tile" onClick={onOpenSettings}>
        <span className="ds-avatar">{user.initials}</span>
        {!collapsed && (
          <span className="min-w-0 flex-1">
            <span
              className="block truncate"
              style={{ fontSize: 'var(--ds-fs-sm)', fontWeight: 500 }}
            >
              {user.name}
            </span>
            <span
              className="block truncate"
              style={{ fontSize: 'var(--ds-fs-xs)', opacity: 0.65 }}
            >
              {user.role}
            </span>
          </span>
        )}
      </button>
    </div>
  </aside>
);
